import { autoinject } from "aurelia-framework";
import { RequestClientService } from "@rightship-tech/common-ui/services/basic/request-client.service";
import { UserSecurityInfoModel, AuthenticationMessageKey } from '../../models/authentication/token.models';
import { Urls } from "../../constants/authentication/user-settings.constants";
import { ArrayUtils } from "../../utilities/arrays";
import { GetDepartmentsResponse } from "../../models/manage/departments.models";
import { CacheClientService, CacheLocation, CacheKeys } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { NoAuthUrls } from "@rightship-tech/common-ui/constants/authentication/no-auth-urls";
import { CultureInfo } from "../../models/internationalization/culture-info.models";
import { CultureInfoService } from "../internationalization/culture-info.service";
import { UrlHelper } from "../../helpers/url-helper";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import * as jstz from 'jstimezonedetect';

@autoinject
export class UserClientService {

    private subscribersOnUserUpdated: ((user: any) => any)[] = [];
    public currentUser: UserSecurityInfoModel;

    constructor(private requestClient: RequestClientService, private cacheClient: CacheClientService, private cultureInfoService: CultureInfoService,
        private configService: ConfigService) { }

    public activateUser(userGuid: string) {
        return new Promise<boolean>((resolve, reject) => {
            this.requestClient.post<boolean>(Urls.ActivateUser, {}, { userGuid: userGuid }).then(r => {
                resolve(r);
            }, reject);
        });
    }

    public doesUserExist(email: string): Promise<boolean> {
        return this.requestClient.get<boolean>(Urls.DoesUserExist, { email });
    }

    public getMe(): Promise<UserSecurityInfoModel> {

        return new Promise<UserSecurityInfoModel>((resolve, reject) => {

            if (this.currentUser) {
                resolve(this.currentUser);
            }
            else {
                this.requestClient.get<UserSecurityInfoModel>(Urls.GetMe, {}).then(r => {
                    this.currentUser = r;
                    this.cacheClient.remove(AuthenticationMessageKey, CacheLocation.SessionStorage);
                    this.refreshUserRegionSettings().then(() => {
                        resolve(r);
                    });
                }, e => {
                    type error = {
                        messages: string[]
                    };
                    try {
                        const response: error = JSON.parse(e.response || null);

                        if (response && response.messages) {
                            this.cacheClient.put(AuthenticationMessageKey, response.messages.join("\n"), CacheLocation.SessionStorage);
                        }
                    } catch { /* siltent catch to handle e.response not being a json response */ }

                    this.refreshUserRegionSettings().then(() => {
                        reject(e);
                    });
                });
            }
        });
    }

    public refreshUserRegionSettings() {
        return new Promise<void>((resolve, reject) => {
            const promise1 = this.cacheClient.put(CacheKeys.timezone, this.getCurrentUserTimeZone(), CacheLocation.SessionStorage);
            const promise2 = this.cacheClient.put(CacheKeys.cultureCode, this.getCurrentUserCulture().code, CacheLocation.SessionStorage);
            Promise.all([promise1, promise2]).then(() => {
                this.requestClient.loadConfigurationHeaders();
                resolve();
            }).catch(reject);
        })
    }

    public getMyAccessibleDepartments() {
        return new Promise<GetDepartmentsResponse>((resolve, reject) => {
            this.requestClient.get<GetDepartmentsResponse>(Urls.GetMyAccessibleDepartments, {}).then(r => {
                resolve(r);
            }, reject);
        });
    }

    public subscribeOnUserUpdate(func: (user: any) => void) {
        this.isListInitialized();
        if (func && typeof func == "function") {
            this.unsubscribeOnUserUpdate(func);
            this.subscribersOnUserUpdated.push(func);
        }
    }

    public unsubscribeOnUserUpdate(func: (user: any) => void) {
        this.isListInitialized();
        const index = ArrayUtils.findIndex(this.subscribersOnUserUpdated, x => x == func);
        if (index > -1) {
            this.subscribersOnUserUpdated.splice(index, 1);
        }
    }

    private isListInitialized() {
        if (!this.subscribersOnUserUpdated) {
            this.subscribersOnUserUpdated = [];
        }
    }

    public reloadUser(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.refreshUser()
                .then(userResponse => {
                    if (this.subscribersOnUserUpdated) {
                        const promises = [];
                        this.subscribersOnUserUpdated.forEach(func => {
                            const possiblePromise = func(userResponse);
                            const isPromise = possiblePromise instanceof Promise;
                            if (isPromise) {
                                promises.push(possiblePromise);
                            }
                        });
                        Promise.all(promises).then(() => {
                            resolve();
                        });
                    } else {
                        resolve();
                    }
                }, e => {
                    // DO NOT MERGE TO GENESIS
                    if (location.href.toLowerCase().indexOf("/resetpassword") == -1) {
                        if (e.content.details == "[PASSWORD_EXPIRED]") {
                            resolve();
                            UrlHelper.redirectForPasswordExpiry();
                        }
                    }
                    reject(e);
                })
                .catch(e => {

                    // DO NOT MERGE TO GENESIS
                    if (location.href.toLowerCase().indexOf("/resetpassword") == -1) {
                        if (e.content.details == "[PASSWORD_EXPIRED]") {
                            resolve();
                            UrlHelper.redirectForPasswordExpiry();
                        }
                    }

                    if (NoAuthUrls.every(url => location.href.toLowerCase().indexOf(url) == -1)) {
                        location.href = location.origin + (this.configService.config.common.auth0Settings.loginPath || '/login') + '?referrer=' + window.location.href;
                    }
                    reject(e);
                });
        });
    }

    public refreshUser(): Promise<UserSecurityInfoModel> {
        this.currentUser = null;
        return this.getMe();
    }

    public getCurrentUserTimeZone() {
        if (this.currentUser && this.currentUser.timezone && this.currentUser.timezone.trim() != "" && this.currentUser.timezone != "null")
            return this.currentUser.timezone;

        return jstz.determine().name();
    }

    public getCurrentUserCulture(): CultureInfo {

        if (this.currentUser && this.currentUser.cultureCode && this.currentUser.cultureCode.trim() != "")
            return this.cultureInfoService.findByCode(this.currentUser.cultureCode);

        let defaultCulture = this.cultureInfoService.findByCode(window.navigator['userLanguage'] || window.navigator.language);
        defaultCulture = defaultCulture != null ? defaultCulture : this.cultureInfoService.findByCode("en-GB");
        return defaultCulture;
    }
}
